import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { useTheme } from "./ThemeProvider";

const REVIEW_ROLES = new Set(["admin", "secretaria"]);
const SNOOZE_MS = 5 * 60 * 1000;
const DUPLICATE_LABELS = {
  exact: "Coincidencia exacta",
  strong_probable: "Muy probable coincidencia",
  soft_probable: "Coincidencia probable",
};

const ENTITY_LABELS = {
  cliente: "Cliente",
  lead: "Lead",
  contacto: "Contacto",
};

function isAllowedRole(usuario) {
  return REVIEW_ROLES.has(String(usuario?.rol || "").toLowerCase());
}

function infoLine(parts = []) {
  return parts.filter(Boolean).join(" · ");
}

export default function PhoneReviewPendingModal({ usuario }) {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  const dismissedRef = useRef(new Map());

  const allowed = isAllowedRole(usuario);

  const visibleItem = useMemo(() => {
    const now = Date.now();
    for (const item of items) {
      const snoozedUntil = dismissedRef.current.get(item.id) || 0;
      if (snoozedUntil <= now) return item;
    }
    return null;
  }, [items]);

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await api.get("/leads/phone-review/pending");
        if (!cancelled) setItems(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    const timer = window.setInterval(load, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [allowed]);

  if (!allowed || !visibleItem) return null;

  const payload = typeof visibleItem.phone_review_payload === "object" ? visibleItem.phone_review_payload : {};
  const candidates = Array.isArray(payload.duplicate_candidates) && payload.duplicate_candidates.length
    ? payload.duplicate_candidates
    : (payload.duplicate_candidate ? [payload.duplicate_candidate] : []);
  const suggested = payload.suggested_assignment || {};

  const openCandidate = (item) => {
    if (!item?.id) return;
    if (item.entity_type === "cliente") {
      navigate(`/clientes/${item.id}`);
      return;
    }
    if (item.entity_type === "contacto") {
      navigate(`/contactos/${item.id}`);
      return;
    }
    navigate(`/pipeline?openLead=${item.id}`);
  };

  const resolveReview = async (action, selectedCandidate = null) => {
    try {
      setBusyAction(action);
      await api.post(`/leads/phone-review/${visibleItem.id}/resolve`, {
        action,
        candidate_id: selectedCandidate?.id || null,
      });
      const res = await api.get("/leads/phone-review/pending");
      setItems(Array.isArray(res.data?.data) ? res.data.data : []);
    } finally {
      setBusyAction("");
    }
  };

  const snooze = () => {
    dismissedRef.current.set(visibleItem.id, Date.now() + SNOOZE_MS);
    setItems((prev) => [...prev]);
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 4000,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div
        style={{
          width: 520,
          maxWidth: "94vw",
          background: c.bgCard,
          borderRadius: 14,
          overflow: "hidden",
          border: `1px solid ${c.border}`,
          boxShadow: isDark ? "0 0 40px rgba(0,0,0,0.55)" : "0 20px 50px rgba(0,0,0,0.22)",
        }}
      >
        <div style={{ height: 3, background: "linear-gradient(90deg, #f59e0b, #ef4444)" }} />
        <div style={{ padding: "18px 20px 0", display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 9,
              background: "linear-gradient(135deg, #f59e0b, #ef4444)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 16,
            }}
          >
            ⚠️
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary }}>
              Revisión telefónica pendiente
            </div>
            <p style={{ fontSize: 12, color: c.textMuted, margin: "8px 0 0", lineHeight: 1.5 }}>
              Este lead automático no se ha asignado todavía porque el teléfono se parece mucho a otro registro.
            </p>
          </div>
          <button
            type="button"
            onClick={snooze}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: c.textMuted, lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: "16px 20px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ borderRadius: 10, border: `1px solid ${c.border}`, padding: 12, background: c.bgApp }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: c.textMuted, textTransform: "uppercase", marginBottom: 6 }}>
              Lead pendiente
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary }}>
              {`${visibleItem.nombre || ""} ${visibleItem.apellido || ""}`.trim() || `Lead #${visibleItem.id}`}
            </div>
            <div style={{ fontSize: 12, color: c.textMuted, marginTop: 4 }}>
              {infoLine([visibleItem.telefono || visibleItem.whatsapp || "Sin teléfono", visibleItem.whatsapp && visibleItem.whatsapp !== visibleItem.telefono ? `WhatsApp ${visibleItem.whatsapp}` : null, visibleItem.email])}
            </div>
          </div>

          <div style={{ borderRadius: 10, border: `1px solid rgba(245,158,11,0.3)`, padding: 12, background: isDark ? "rgba(245,158,11,0.08)" : "#fffbeb" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: c.textMuted, textTransform: "uppercase", marginBottom: 6 }}>
              {candidates.length > 1 ? "Coincidencias probables" : "Coincidencia probable"}
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              {candidates.map((item) => (
                <div key={`${item.entity_type}-${item.id}`} style={{ borderRadius: 8, border: `1px solid ${c.border}`, padding: 10, background: c.bgCard }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary }}>
                    {item.display_name || "Registro existente"}
                  </div>
                  <div style={{ fontSize: 11, color: c.textMuted, marginTop: 4 }}>
                    {ENTITY_LABELS[item.entity_type] || item.entity_type || "Registro"}
                  </div>
                  <div style={{ fontSize: 12, color: c.textMuted, marginTop: 4 }}>
                    {infoLine([
                      item.telefono || "Sin teléfono",
                      item.whatsapp && item.whatsapp !== item.telefono ? `WhatsApp ${item.whatsapp}` : null,
                      item.email,
                    ])}
                  </div>
                  {infoLine([
                    item.etapa ? `Etapa ${item.etapa}` : null,
                    item.fuente ? `Fuente ${item.fuente}` : null,
                    item.propiedad_ref ? `Ref. ${item.propiedad_ref}` : null,
                    item.pais ? `País ${item.pais}` : null,
                  ]) && (
                    <div style={{ fontSize: 12, color: c.textMuted, marginTop: 4 }}>
                      {infoLine([
                        item.etapa ? `Etapa ${item.etapa}` : null,
                        item.fuente ? `Fuente ${item.fuente}` : null,
                        item.propiedad_ref ? `Ref. ${item.propiedad_ref}` : null,
                        item.pais ? `País ${item.pais}` : null,
                      ])}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: c.textMuted, marginTop: 6 }}>
                    {DUPLICATE_LABELS[item.duplicate_type] || "Coincidencia probable"}
                    {Number.isFinite(item.confidence) ? ` · confianza ${item.confidence}%` : ""}
                    {item.reason ? ` · ${item.reason}` : ""}
                  </div>
                  {item.agente_nombre && (
                    <div style={{ fontSize: 12, color: c.textMuted, marginTop: 6 }}>
                      Agente actual del parecido: {item.agente_nombre}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                    <button
                      type="button"
                      onClick={() => openCandidate(item)}
                      style={{
                        padding: "8px 10px",
                        borderRadius: 8,
                        border: `1px solid ${c.border}`,
                        background: c.bgApp,
                        color: c.textPrimary,
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      Abrir ficha
                    </button>
                    <button
                      type="button"
                      disabled={!item.agente_id || !!busyAction}
                      onClick={() => resolveReview("assign_matched_agent", item)}
                      style={{
                        padding: "8px 10px",
                        borderRadius: 8,
                        border: "none",
                        background: !item.agente_id || busyAction ? c.bgInput : "linear-gradient(135deg, #2563eb, #1d4ed8)",
                        color: !item.agente_id || busyAction ? c.textMuted : "#fff",
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: !item.agente_id || busyAction ? "not-allowed" : "pointer",
                      }}
                    >
                      {busyAction === "assign_matched_agent"
                        ? "Asignando..."
                        : `Asignar a ${item.agente_nombre || "su agente"}`}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {suggested?.motivo && (
              <div style={{ fontSize: 12, color: c.textMuted, marginTop: 6 }}>
                Reglas automáticas: {suggested.motivo}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => navigate(`/pipeline?openLead=${visibleItem.id}`)}
              style={{
                padding: "9px 12px",
                borderRadius: 8,
                border: `1px solid ${c.border}`,
                background: c.bgApp,
                color: c.textPrimary,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Abrir lead
            </button>
            <button
              type="button"
              disabled={!!busyAction}
              onClick={() => resolveReview("assign_by_rules")}
              style={{
                flex: 1,
                minWidth: 180,
                padding: "10px 12px",
                borderRadius: 8,
                border: `1px solid ${c.border}`,
                background: c.bgCard,
                color: c.textPrimary,
                fontSize: 12,
                fontWeight: 700,
                cursor: busyAction ? "wait" : "pointer",
              }}
            >
              {busyAction === "assign_by_rules" ? "Reasignando..." : "Reasignar según normas"}
            </button>
          </div>

          <div style={{ fontSize: 11, color: c.textMuted }}>
            Mientras no se resuelva, este lead permanece sin asignación comercial automática.
            {loading ? " Actualizando..." : ""}
          </div>
        </div>
      </div>
    </div>
  );
}
